import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useContentSection } from "@/context/ContentContext";
import { NAV } from "@/constants/testIds";
import { Menu, X, ShoppingBag } from "lucide-react";

const links = [
    { to: "/", label: "Home", id: NAV.home },
    { to: "/order", label: "Menu", id: NAV.menu },
    { to: "/specials", label: "Specials", id: NAV.specials },
    { to: "/about", label: "About", id: NAV.about },
    { to: "/contact", label: "Contact", id: NAV.contact },
];

export default function Header() {
    const { count, setOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const g = useContentSection("global");
    const logo = g.logo_url || "/brand/umami-logo.png";
    const halaal = g.halaal_logo_url || "/brand/halaal.webp";
    const showHalaal = g.show_halaal_header !== false;

    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-brand-ink/80 border-b border-brand-line">
            <div className="container-wide flex items-center justify-between h-16 md:h-20">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
                    <img src={logo} alt="Umami Kraft" className="h-14 md:h-16 w-auto object-contain [filter:invert(1)_hue-rotate(180deg)_saturate(1.4)]" />
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.id}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors ${
                                    isActive
                                        ? "text-brand-gold"
                                        : "text-brand-cream/80 hover:text-brand-gold"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {showHalaal && (
                        <div
                            data-testid="header-halaal"
                            className="hidden sm:flex items-center gap-2 pr-3 border-r border-brand-line"
                            title={`${g.halaal_caption || "HALAAL CERTIFIED"} · ${g.halaal_subcaption || ""}`}
                        >
                            <img src={halaal} alt="Halaal Certified" className="h-9 md:h-10 w-auto object-contain" />
                            <div className="hidden lg:block leading-tight">
                                <p className="text-[10px] tracking-[0.2em] uppercase text-brand-gold">{g.halaal_caption || "Halaal Certified"}</p>
                                <p className="text-[10px] text-brand-cream/60">{g.halaal_subcaption || ""}</p>
                            </div>
                        </div>
                    )}
                    <button
                        data-testid={NAV.cartBtn}
                        onClick={() => setOpen(true)}
                        className="relative inline-flex items-center gap-2 border border-brand-line hover:border-brand-gold text-brand-cream px-4 py-2 transition-colors rounded-sm"
                    >
                        <ShoppingBag size={16} />
                        <span className="hidden sm:inline text-sm">Cart</span>
                        {count > 0 && (
                            <span
                                data-testid={NAV.cartCount}
                                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-brand-olive text-brand-cream text-xs rounded-sm"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid={NAV.mobileToggle}
                        className="md:hidden text-brand-cream p-2"
                        onClick={() => setMobileOpen((v) => !v)}
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="md:hidden border-t border-brand-line bg-brand-ink">
                    <div className="container-wide py-4 flex flex-col gap-3">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `text-base py-2 border-b border-brand-line ${
                                        isActive ? "text-brand-gold" : "text-brand-cream/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
